import * as Instancia from '../constantes/InstanciaPartido.js';
import * as DTOs from '../DTOs/PartidoFixtureDTOs.js';

/**
 * Calcula cuántos partidos se esperan en una etapa de definición
 * @param {Object} etapa - Objeto etapa con cantidadCruces y cantidadRuedas
 * @returns {number} Cantidad total de partidos esperados
 */
export const CantidadPartidosEsperadosDefinicion = (etapa) => {
  if (!etapa || !etapa.cantidadCruces || !etapa.cantidadRuedas) {
    return 0;
  }
  // En definición: cantidadCruces * cantidadRuedas
  // Si cantidadRuedas = 1 → cantidadCruces partidos únicos
  // Si cantidadRuedas = 2 → cantidadCruces partidos de ida + cantidadCruces partidos de vuelta
  return etapa.cantidadCruces * etapa.cantidadRuedas;
};

/**
 * Genera partidos vacíos para una etapa de definición
 * @param {Object} contexto - Datos del contexto (torneo, categoría, etapa)
 * @param {number} startId - ID temporal inicial (negativo) para los partidos generados
 * @returns {Array} Array de partidos vacíos
 */
export const GenerarPartidosDefinicion = (contexto, startId = -1) => {
  const etapa = contexto.etapa;
  if (!etapa || etapa.definicion !== true) {
    return [];
  }

  const partidos = [];
  const cantidadCruces = etapa.cantidadCruces || 0;
  const cantidadRuedas = etapa.cantidadRuedas || 1;
  let idTemporal = startId;

  if (cantidadRuedas === 1) {
    // Partidos únicos
    for (let i = 0; i < cantidadCruces; i++) {
      partidos.push(
        DTOs.PartidoFixtureVacio(contexto, idTemporal--, Instancia.UNICO, i, null, null)
      );
    }
  } else if (cantidadRuedas === 2) {
    // Partidos de ida
    for (let i = 0; i < cantidadCruces; i++) {
      partidos.push(DTOs.PartidoFixtureVacio(contexto, idTemporal--, Instancia.IDA, i, null, null));
    }
    // Partidos de vuelta
    for (let i = 0; i < cantidadCruces; i++) {
      partidos.push(
        DTOs.PartidoFixtureVacio(contexto, idTemporal--, Instancia.VUELTA, i, null, null)
      );
    }
  }

  return partidos;
};

/**
 * Identifica qué partidos faltan en una etapa de definición
 * @param {Array} partidosExistentes - Partidos ya creados en la base de datos
 * @param {Object} contexto - Datos del contexto (torneo, categoría, etapa)
 * @returns {Array} Array de partidos vacíos para los que faltan
 */
export const PartidosFaltantesDefinicion = (partidosExistentes, contexto) => {
  const etapa = contexto.etapa;
  if (!etapa || etapa.definicion !== true) {
    return [];
  }

  const existentes = partidosExistentes || [];
  const cantidadCruces = etapa.cantidadCruces || 0;
  const cantidadRuedas = etapa.cantidadRuedas || 1;

  // Contar cuántos partidos ya existen por instancia
  const contadores = {
    [Instancia.UNICO]: 0,
    [Instancia.IDA]: 0,
    [Instancia.VUELTA]: 0,
  };

  existentes.forEach((partido) => {
    if (partido.codigoInstancia && contadores[partido.codigoInstancia] !== undefined) {
      contadores[partido.codigoInstancia]++;
    }
  });

  const faltantes = [];
  let idTemporal = -1;

  if (cantidadRuedas === 1) {
    // Generar partidos únicos faltantes
    const faltantesUnicos = cantidadCruces - contadores[Instancia.UNICO];
    for (let i = 0; i < faltantesUnicos; i++) {
      faltantes.push(
        DTOs.PartidoFixtureVacio(contexto, idTemporal--, Instancia.UNICO, i, null, null)
      );
    }
  } else if (cantidadRuedas === 2) {
    // Generar partidos de ida faltantes
    const faltantesIda = cantidadCruces - contadores[Instancia.IDA];
    for (let i = 0; i < faltantesIda; i++) {
      faltantes.push(
        DTOs.PartidoFixtureVacio(contexto, idTemporal--, Instancia.IDA, i, null, null)
      );
    }
    // Generar partidos de vuelta faltantes
    const faltantesVuelta = cantidadCruces - contadores[Instancia.VUELTA];
    for (let i = 0; i < faltantesVuelta; i++) {
      faltantes.push(
        DTOs.PartidoFixtureVacio(contexto, idTemporal--, Instancia.VUELTA, i, null, null)
      );
    }
  }

  return faltantes;
};

/**
 * Agrupa partidos de definición por instancia (Único, Ida, Vuelta)
 * @param {Array} partidos - Array de partidos (existentes y vacíos)
 * @returns {Object} Objeto con keys IDA, VUELTA, UNICO y arrays de partidos
 */
export const AgruparPartidosPorInstancia = (partidos) => {
  const grupos = {
    [Instancia.UNICO]: [],
    [Instancia.IDA]: [],
    [Instancia.VUELTA]: [],
  };

  partidos.forEach((partido) => {
    const instancia = partido.codigoInstancia;
    if (grupos[instancia]) {
      grupos[instancia].push(partido);
    }
  });

  return grupos;
};

/**
 * Replica un equipo de partido de ida a partido de vuelta (invertido)
 * @param {Object} partidoIda - Partido de ida con equipos asignados
 * @param {Object} partidoVuelta - Partido de vuelta a actualizar
 * @param {string} campo - 'equipoLocal' o 'equipoVisitante'
 * @returns {Object} Partido de vuelta actualizado
 */
export const ReplicarEquipoIdaVuelta = (partidoIda, partidoVuelta, campo) => {
  if (!partidoIda || !partidoVuelta) {
    return partidoVuelta;
  }

  const partidoActualizado = { ...partidoVuelta };

  // El local de ida se convierte en visitante de vuelta, y viceversa
  if (campo === 'equipoLocal') {
    partidoActualizado.equipoVisitante = partidoIda.equipoLocal;
  } else if (campo === 'equipoVisitante') {
    partidoActualizado.equipoLocal = partidoIda.equipoVisitante;
  }

  return partidoActualizado;
};

/**
 * Ordena partidos por ordenPartido
 * @param {Array} partidos - Array de partidos
 * @returns {Array} Array ordenado
 */
export const OrdenarPartidos = (partidos) => {
  return [...partidos].sort((a, b) => (a.ordenPartido || 0) - (b.ordenPartido || 0));
};
